import React from 'react';
import { motion } from 'framer-motion';
import { ArrowDownIcon, ArrowRightIcon, CircleCheckIcon, DatabaseIcon, FileCheckIcon, FileTextIcon, ShieldCheckIcon, SparklesIcon, UserIcon, UsersIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { trustCommitments, trustSolutionCopy as c, trustStages, type Bi, type CommitmentIcon, type StageIcon } from '../../data/trustSolution';
import { TrustSystemVisual } from './TrustSystemVisual';
import { TrustExample } from './TrustExample';
const EASE = [0.23, 1, 0.32, 1] as const;
const reveal = {
  initial: {
    opacity: 0,
    y: 12
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.3,
    ease: EASE
  }
};
const STAGE_ICONS: Record<StageIcon, typeof UserIcon> = {
  identity: UserIcon,
  context: FileCheckIcon,
  verify: DatabaseIcon,
  evaluate: SparklesIcon,
  recommend: FileTextIcon,
  act: CircleCheckIcon
};
const COMMITMENT_ICONS: Record<CommitmentIcon, typeof UserIcon> = {
  shield: ShieldCheckIcon,
  doc: FileCheckIcon,
  users: UsersIcon,
  user: UserIcon
};
export function TrustSolution() {
  const {
    lang
  } = useLanguage();
  const pick = (b: Bi) => lang === 'FR' ? b.fr : b.en;
  return <section id="gouvernance" className="relative overflow-hidden bg-white border-t border-[#EEF0F4] px-6 py-16 lg:py-20 scroll-mt-28" aria-labelledby="trust-solution-title" data-id="element-7120" data-mp="2pbdfqvhsctey">
      <div className="max-w-[1400px] mx-auto" data-id="element-7121" data-mp="3h0ynnsynzk9j">
        {/* Promise + trust system */}
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_1fr] gap-10 xl:gap-8 items-center" data-id="element-7122" data-mp="3lyrt2js58a8r">
          <motion.div {...reveal} data-id="element-7123" data-mp="3873iikxjlttw">
            <p className="font-sans text-[12.5px] font-semibold tracking-[0.2em] uppercase text-[#4F46E5]" data-id="element-7124" data-mp="38624q8p56t8h">{pick(c.eyebrow)}</p>
            <h2 id="trust-solution-title" className="mt-4 font-serif text-[2.3rem] sm:text-[3rem] lg:text-[3.4rem] 2xl:text-[3.7rem] leading-[1.03] tracking-tight" data-id="element-7125" data-mp="3t52579eopp1">
              {c.headline.map(l => <span key={l.text.fr} className={`block ${l.accent ? 'text-[#4F46E5]' : 'text-[#121A3A]'}`} data-id="element-7126" data-mp="ign9kdecryf5">{pick(l.text)}</span>)}
            </h2>
            <p className="mt-5 max-w-[620px] font-sans text-[16px] md:text-[18px] text-[#5A6178] leading-relaxed" data-id="element-7127" data-mp="ecxnbul5hsxa">{pick(c.body)}</p>
          </motion.div>
          <motion.div {...reveal} data-id="element-7128" data-mp="4fj9m2qm3i8o">
            <TrustSystemVisual pick={pick} data-id="element-7129" data-mp="4iov780181zn" />
          </motion.div>
        </div>

        {/* Six-stage trust journey */}
        <motion.ol {...reveal} className="mt-10 grid grid-cols-1 md:grid-cols-3 xl:flex xl:items-stretch gap-3 xl:gap-2" data-id="element-7130" data-mp="3qw6zp3jq90pz">
          {trustStages.map((s, i) => {
          const Icon = STAGE_ICONS[s.icon];
          return <React.Fragment key={s.icon} data-id="element-7131" data-mp="3hwjvk9zm5gt8" data-mpi={i}>
                <li className="relative xl:flex-1 min-w-0 rounded-xl border border-[#EEEFF4] bg-white px-4 py-4 text-center flex flex-col items-center" data-id="element-7132" data-mp="3hte80z6mumzm">
                  <span className="absolute left-3 top-3 w-6 h-6 rounded-full bg-[#F1F0FD] flex items-center justify-center font-sans text-[11.5px] font-semibold text-[#121A3A]" data-id="element-7133" data-mp="37ychbywplcux">
                    {i + 1}
                  </span>
                  <span className="w-10 h-10 rounded-full bg-[#F1F0FD] flex items-center justify-center" data-id="element-7134" data-mp="32qmipw6rsxe4">
                    <Icon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.7} aria-hidden="true" data-id="element-7135" data-mp="37ph9bkazbq8s" />
                  </span>
                  <p className="mt-2.5 font-sans text-[13.5px] font-bold tracking-[0.04em] uppercase text-[#121A3A]" data-id="element-7136" data-mp="2f2wm8w1je0b1">{pick(s.verb)}</p>
                  <p className="font-sans text-[14px] font-semibold text-[#121A3A]" data-id="element-7137" data-mp="2k312ckyj8t0q">{pick(s.title)}</p>
                  <p className="mt-1.5 font-sans text-[13px] text-[#6B7288] leading-snug" data-id="element-7138" data-mp="2p016gqbtnlp7">{pick(s.question)}</p>
                </li>
                {i < trustStages.length - 1 && <li className="hidden xl:flex items-center" aria-hidden="true" data-id="element-7139" data-mp="1wd6pw4i4l6h0">
                    <ArrowRightIcon className="w-4 h-4 text-[#4F46E5]" strokeWidth={2} data-id="element-7140" data-mp="21b8aitv79dul" />
                  </li>}
                {i < trustStages.length - 1 && <li className="md:hidden flex justify-center" aria-hidden="true" data-id="element-7141" data-mp="6sx1sxl8sbg9">
                    <ArrowDownIcon className="w-4 h-4 text-[#4F46E5]" strokeWidth={2} data-id="element-7142" data-mp="1u3ob55ygkg3" />
                  </li>}
              </React.Fragment>;
        })}
        </motion.ol>

        {/* One concrete example */}
        <motion.div {...reveal} className="mt-6" data-id="element-7143" data-mp="pl81kiklnta5">
          <TrustExample pick={pick} data-id="element-7144" data-mp="pjy0dkiga4za" />
        </motion.div>

        {/* Commitments */}
        <motion.div {...reveal} id="engagements" className="mt-10 border-t border-[#EEEFF4] pt-6 scroll-mt-36" data-id="element-7145" data-mp="ljmgwb1tqape">
          <p className="font-sans text-[11.5px] font-semibold tracking-[0.2em] uppercase text-[#4F46E5]" data-id="element-7146" data-mp="zj5n2yb2rbz2">{pick(trustCommitments.eyebrow)}</p>
          <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-y-6 lg:divide-x lg:divide-[#EEEFF4]" data-id="element-7147" data-mp="31b1wa19rs9qo">
            {trustCommitments.items.map(it => {
            const Icon = COMMITMENT_ICONS[it.icon];
            return <li key={it.title.fr} className="flex items-start gap-4 lg:px-6 first:lg:pl-0" data-id="element-7148" data-mp="1buojnztb9wmp">
                  <span className="w-12 h-12 rounded-full bg-[#F1F0FD] flex items-center justify-center shrink-0" data-id="element-7149" data-mp="1gsq3imyk9zz8">
                    <Icon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.7} aria-hidden="true" data-id="element-7150" data-mp="122agr3qhyjyy" />
                  </span>
                  <div data-id="element-7151" data-mp="t3oq0lavfyn3">
                    <p className="font-sans text-[14px] font-semibold text-[#121A3A]" data-id="element-7152" data-mp="t3f8lm3w6zt0">{pick(it.title)}</p>
                    <p className="mt-1 font-sans text-[13px] text-[#6B7288] leading-snug" data-id="element-7153" data-mp="y3t7adjlsxed">{pick(it.text)}</p>
                  </div>
                </li>;
          })}
          </ul>
        </motion.div>
      </div>
    </section>;
}